import type { SearchViewProps, GameProps, CategoryProps } from '../../types';

import * as React from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useAppContext } from '../../contexts/AppContext';
import { useTranslation } from 'next-i18next';
import { useTheme } from '@mui/material/styles';
import { useHighlightHandlers } from '../../contexts/HighlightContext/Highlight';
import { SearchResultsGrid } from './SearchResultsGrid';
import { BreadCrumbs } from '../../components/BreadCrumbs';
import { Title } from '../../components/Title';
import { Link } from '../../components/Link';
import { Typography as MuiTypography } from '@mui/material';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Container from '@mui/material/Container';

const Typography = React.forwardRef<HTMLInputElement, any>((props, ref) =>
  useHighlightHandlers(MuiTypography, { forwardedRef: ref, ...props })
);

const LoaderContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '50vh',
}));

const ItemList = styled('div')(() => ({}));

const PageContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
}));

const ItemLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

export const setCollectionsByGames = (games: GameProps[] | undefined) => {
  if (!games) return [];
  const collections: CategoryProps[] = [];
  games.forEach((game: GameProps) => {
    game.categories?.forEach((item: CategoryProps) => {
      const result = collections.find((category: CategoryProps) => category.id === item.id);
      if (result === undefined) collections.push(item);
    });
  });
  return collections;
};

export const SearchA = (props: SearchViewProps) => {
  const { games, pageContext } = props;
  const router = useRouter();
  const theme = useTheme();
  const keyword = router?.query?.q?.toString().replace(/[^\s\w]/gi, '') || '';
  const { t } = useTranslation('search_a');
  const appContext = useAppContext();
  const { results, isLoading, isError, setKeyword } = appContext.useSearch();
  const labelRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (router?.query?.q) {
      setKeyword(router.query.q.toString().replace(/[^\s\w]/gi, ''));
    }
  }, [router?.query?.q]);

  const items = React.useMemo<GameProps[] | undefined>(() => {
    return !results || results.length <= 0 ? games : results;
  }, [results]);

  const collections = React.useMemo<CategoryProps[]>(() => {
    return setCollectionsByGames(items);
  }, [items]);

  const loading = React.useMemo<boolean>(() => {
    return isLoading && Boolean(keyword);
  }, [isLoading, keyword]);

  return (
    <PageContainer>
      <Container maxWidth={false} disableGutters={true}>
        <BreadCrumbs breadcrumbs={pageContext.breadcrumbs} />
      </Container>
      {isError ||
        ((!results || results.length <= 0) && (
          <Container maxWidth={false} sx={{ margin: theme.spacing(2, 0) }}>
            <Stack spacing={2}>
              <>
                {isError && <Alert severity="error">{JSON.stringify(isError)}</Alert>}
                {!results ||
                  (results.length <= 0 && <Alert severity="info">{t('no_results_any', 'Nothing found...')}</Alert>)}
              </>
            </Stack>
          </Container>
        ))}
      {loading ? (
        <LoaderContainer>
          <CircularProgress size="2rem" color="secondary" />
        </LoaderContainer>
      ) : (
        <Grid spacing={2}>
          <Grid item xs={12} sm={8} lg={12}>
            <SearchResultsGrid items={items} emptyItems={18}>
              <Title component="h1" variant="h1">
                {t('title_games', 'Search')}
              </Title>
            </SearchResultsGrid>
          </Grid>
        </Grid>
      )}
    </PageContainer>
  );
};
